// src/components/nutrition/nutritionData.jsx

import {
  vitaminOptions,
  vitaminInitialData,
  VITAMIN_FOCUS_IDS,
  buildVitaminDataFromNutritionCalculateSnapshot,
  getVitaminsGroup,
  getVitaminStatuses,
  getVitaminQuickAddBehaviorFor,
  getVitaminQuickAddDefaults,
  buildVitaminSpecialTile,
  getVitaminLogAction,
  getVitaminChartData,
} from "./vitamins/nutritionVitamin";

import {
  mineralOptions,
  mineralInitialData,
  buildMineralDataFromNutritionCalculateSnapshot,
  getMineralsGroup,
  getMineralStatuses,
  getMineralFocusEntries,
  buildMineralSpecialTile,
  getMineralChartData,
} from "./minerals/nutritionMinerals";

import {
  fatOptions,
  fatInitialData,
  buildFatDataFromNutritionCalculateSnapshot,
  getFatsGroup,
  getFatStatuses,
  getFatFocusEntries,
  buildFatSpecialTile,
  getFatLogAction,
  getFatChartData,
} from "./fats/nutritionFats";

import {
  fitnessBodyOptions,
  fitnessBodyInitialData,
  getFitnessBodyGroups,
  getFitnessBodyOverviewMetas,
  getFitnessBodyStatuses,
  getFitnessBodyFocusEntries,
  buildFitnessBodySpecialTile,
} from "./fitnessbodycomp/nutritionFitnessBodyComp";

// Chart data for the three main nutrition categories.
export function getChartData(dataInput) {
  const data = dataInput || initialData;

  return {
    vitamins: getVitaminChartData(data, {
      low: DV_OK_LOW,
      high: DV_OK_HIGH,
    }),
    minerals: getMineralChartData(data, {
      low: DV_OK_LOW,
      high: DV_OK_HIGH,
    }),
    fats: getFatChartData(data),
  };
}

export const DV_OK_LOW = 80;
export const DV_OK_HIGH = 120;
export const KNA_TARGET = 2.0;
export const SKIP_TRIAL_REDIRECT = "phn:skipTrialRedirect";

export const ICON_METADATA = {
  size: 18,
};

const VITAMIN_IDS = new Set(vitaminOptions.map((option) => option.id));
const MINERAL_IDS = new Set(mineralOptions.map((option) => option.id));
const FAT_IDS = new Set(fatOptions.map((option) => option.id));

const CATEGORY_ROUTES = {
  fats: "/nutrition/fats",
  vitamins: "/nutrition/vitamins",
  minerals: "/nutrition/minerals",
  other: "/nutrition",
};

export function getCategoryForId(id) {
  if (FAT_IDS.has(id)) return "fats";
  if (VITAMIN_IDS.has(id)) return "vitamins";
  if (MINERAL_IDS.has(id)) return "minerals";
  return "other";
}

export function getRouteForId(id) {
  const category = getCategoryForId(id);

  return category === "other"
    ? `/nutrition/items/${encodeURIComponent(id)}`
    : CATEGORY_ROUTES[category] || CATEGORY_ROUTES.other;
}

export function getIconTypeForId(id) {
  return getCategoryForId(id);
}

export const uiText = {
  screenTitle: "My Nutrition",
  nutritionDistTitle: "Nutrition Distribution",
  nutritionDistSub: "Interactive two-ring visualization",
  todayFocusTitle: "Today Focus",
  todayFocusSub: "Auto-sorted by need",
  insightsTitle: "How is my logged nutrition intake affecting my body?",
  categoryOverviewTitle: "Category Overview",
  navDashboard: "Dashboard",
  navCalculators: "Calculators",
  navLogs: "Logs",
  navInsights: "Insights",
  trialPrefix: "Trial:",
  trialDays: (value) => `day${value === 1 ? "" : "s"} left`,
  upgradeCta: "Upgrade",
  upgradeAria: "Upgrade plan",
  prevDayAria: "Previous day",
  nextDayAria: "Next day",
  notificationsAria: "Notifications",
  settingsAria: "Settings",
  quickAddAria: "Quick Add",
  quickAddTitle: "Quick Add",
  quickAddAmountLabel: "Amount",
  quickAddNudgeNote: "This will nudge today's %DV by a small amount.",
  quickAddHint: "We'll remember your last action next time.",
  cancel: "Cancel",
  logIntakeCta: "Log intake",
  ranges: ["today", "7d", "30d"],
};

export const allNutrientOptions = [
  ...fitnessBodyOptions,
  ...mineralOptions,
  ...vitaminOptions,
  ...fatOptions,
];

export function knaStatus(ratio) {
  if (Number.isNaN(ratio)) return "data-needed";
  if (ratio >= KNA_TARGET) return "on-track";
  return "low";
}

export function dvStatus(percent) {
  if (percent == null || Number.isNaN(percent)) return "data-needed";
  if (percent < DV_OK_LOW) return "low";
  if (percent > DV_OK_HIGH) return "high";
  return "on-track";
}

export function ePctStatus(value, minimum, maximum) {
  if (value == null || Number.isNaN(value)) return "data-needed";
  if (minimum != null && value < minimum) return "low";
  if (maximum != null && value > maximum) return "high";
  return "on-track";
}

/**
 * Map a status key (from getStatuses / getFitnessBodyStatuses) to a badge
 * label + intent used by the nutrition UI kit.
 *
 * Supports:
 * - "data-needed"
 * - "ok" / "on-track"
 * - "low" / "extreme-low"
 * - "high" / "extreme-high"
 * - "caution"
 * - "at-risk" (BMI overweight)
 * - "off-track" (BMI obesity)
 */
export function statusToBadge(statusKey) {
  const safeKey =
    typeof statusKey === "string" && statusKey.trim().length > 0
      ? statusKey.trim()
      : "data-needed";

  const mapping = {
    "data-needed": {
      label: "Data needed",
      intent: "muted",
    },
    ok: {
      label: "On track",
      intent: "success",
    },
    "on-track": {
      label: "On track",
      intent: "success",
    },
    low: {
      label: "Low",
      intent: "warn",
    },
    "extreme-low": {
      label: "Very low",
      intent: "danger",
    },
    high: {
      label: "High",
      intent: "danger",
    },
    "extreme-high": {
      label: "Very high",
      intent: "danger",
    },
    caution: {
      label: "Caution",
      intent: "warn",
    },
    "at-risk": {
      label: "At risk",
      intent: "warn",
    },
    "off-track": {
      label: "Off track",
      intent: "danger",
    },
  };

  return mapping[safeKey] || mapping["data-needed"];
}

export const initialData = {
  ...fitnessBodyInitialData,
  ...mineralInitialData,
  ...vitaminInitialData,
  ...fatInitialData,
};

/**
 * Build the override map used by effectiveNutritionData from a single
 * nutritionTargetsCalculations snapshot. Vitamins, minerals, and fats all
 * plug into the same override mechanism.
 */
export function buildNutritionDataOverridesFromCalculateSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") {
    return null;
  }

  const vitaminOverrides =
    buildVitaminDataFromNutritionCalculateSnapshot(snapshot, null) || {};
  const mineralOverrides =
    buildMineralDataFromNutritionCalculateSnapshot(snapshot, null) || {};
  const fatOverrides =
    buildFatDataFromNutritionCalculateSnapshot(snapshot, null) || {};

  const mergedOverrides = {
    ...vitaminOverrides,
    ...mineralOverrides,
    ...fatOverrides,
  };

  if (Object.keys(mergedOverrides).length === 0) {
    return null;
  }

  return mergedOverrides;
}

// Format the main numeric amount and its sub-label for the ring hero.
const formatAmountForDisplay = (node) => {
  if (!node) return { mainText: "-", subText: "" };

  if (node.rae != null) {
    return {
      mainText: `${node.rae} µg`,
      subText: "RAE",
    };
  }

  const rangeDays =
    typeof node.rangeDays === "number" && Number.isFinite(node.rangeDays)
      ? node.rangeDays
      : null;

  const rangeLabel =
    rangeDays == null ? "today" : `${rangeDays} ${rangeDays === 1 ? "day" : "days"}`;

  if (node.mcg != null) {
    return {
      mainText: `${node.mcg} µg`,
      subText: rangeLabel,
    };
  }

  if (node.mg != null) {
    return {
      mainText: `${node.mg} mg`,
      subText: rangeLabel,
    };
  }

  if (node.g != null) {
    return {
      mainText: `${node.g} g`,
      subText: rangeLabel,
    };
  }

  if (node.iu != null) {
    return {
      mainText: `${node.iu} IU`,
      subText: rangeLabel,
    };
  }

  if (node.value != null && node.unit) {
    return {
      mainText: String(node.value),
      subText: node.unit,
    };
  }

  if (node.value != null) {
    return {
      mainText: String(node.value),
      subText: "",
    };
  }

  return { mainText: "-", subText: "" };
};

const ringStrokeByStatus = (status) => {
  if (status === "low" || status === "at-risk") return "#f59e0b"; // amber
  if (status === "high" || status === "off-track") return "#ef4444"; // red
  if (status === "on-track") return "#10b981"; // green
  return "#6b7280"; // grey
};

const inferUnitFromNode = (node) => {
  if (!node || typeof node !== "object") return null;

  if (typeof node.nnrUnit === "string") return node.nnrUnit;

  if (
    node.rae != null ||
    node.mcg != null ||
    node.folateMcg != null ||
    node.b12Mcg != null
  ) {
    return "µg";
  }

  if (node.mg != null) return "mg";
  if (node.g != null) return "g";
  if (node.iu != null) return "IU";
  if (typeof node.unit === "string") return node.unit;

  return null;
};

const formatAmountWithUnit = (value, unitHint) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;

  const unit = unitHint || "";
  const roundedValue =
    Math.abs(numericValue) >= 100
      ? Math.round(numericValue)
      : Math.round(numericValue * 10) / 10;

  return `${roundedValue}${unit ? unit : ""}`;
};

const formatBasisLabel = (basis) => {
  switch (basis) {
    case "AI":
      return "Target (AI)";
    case "RI":
      return "Target (RI)";
    case "RI_per_MJ":
    case "RI_per_MJ_NE":
      return "Target (RI/MJ)";
    case "CDRR":
      return "Limit (CDRR)";
    case "UL_only":
      return "Upper level";
    default:
      return "Target";
  }
};

export function getNutrientTargetDetail(node) {
  if (!node) return null;

  const amount = node.nnrTarget;
  if (amount == null) return null;

  const unit = inferUnitFromNode(node);
  const amountText = formatAmountWithUnit(amount, unit);
  if (!amountText) return null;

  const label = formatBasisLabel(node.nnrBasis);
  return `${label}: ${amountText}`;
}

export function getNutrientUpperLevelDetail(node) {
  if (!node) return null;

  const upper = node.nnrUpperLevel;
  if (upper == null) return null;

  const unit = inferUnitFromNode(node);
  const amountText = formatAmountWithUnit(upper, unit);
  if (!amountText) return null;

  const usedPercentText = Number.isFinite(node.nnrPctOfUpperLevel)
    ? ` (${Math.round(node.nnrPctOfUpperLevel)}% used)`
    : "";

  return `Upper level: ${amountText}${usedPercentText}`;
}

export function getStatuses(dataInput) {
  const data = dataInput || initialData;
  const statusMap = {};

  Object.assign(statusMap, getFitnessBodyStatuses(data));

  Object.assign(
    statusMap,
    getMineralStatuses(data, {
      dvStatusFn: dvStatus,
      knaStatusFn: knaStatus,
    }),
  );

  Object.assign(
    statusMap,
    getVitaminStatuses(data, {
      dvStatusFn: dvStatus,
      low: DV_OK_LOW,
      high: DV_OK_HIGH,
    }),
  );

  Object.assign(
    statusMap,
    getFatStatuses(data, {
      ePctStatusFn: ePctStatus,
    }),
  );

  return statusMap;
}

export function getFocusOrder(dataInput) {
  const data = dataInput || initialData;
  const entries = [];

  const pushEntry = (id, score) => {
    entries.push({ id, score });
  };

  getFitnessBodyFocusEntries(data).forEach((entry) => {
    entries.push(entry);
  });

  getMineralFocusEntries(data).forEach((entry) => {
    entries.push(entry);
  });

  VITAMIN_FOCUS_IDS.forEach((id) => {
    const score = data[id]?.todayDV ?? data[id]?.folateDV ?? 100;
    pushEntry(id, score);
  });

  getFatFocusEntries(data).forEach((entry) => {
    entries.push(entry);
  });

  return entries
    .sort((left, right) => left.score - right.score)
    .map((entry) => entry.id)
    .slice(0, 6);
}

// -----------------------------------------------------------------------------
// Category overview builder
// -----------------------------------------------------------------------------

// Summarises vitamins, minerals and fats into high-level category cards.
// Each card answers:
//  - How well is this category covered on average?
//  - How many nutrients are low / high or caution?
class CategoryOverviewBuilder {
  constructor({ nutritionData, statuses }) {
    this.nutritionData =
      nutritionData && typeof nutritionData === "object"
        ? nutritionData
        : {};
    this.statuses = statuses || {};
  }

  /**
   * Prefer a range-based %DV, fall back to today's %DV.
   */
  getPercentOfTargetForNode(node) {
    if (!node || typeof node !== "object") {
      return null;
    }

    const rangePercent =
      typeof node.rangeDV === "number" ? node.rangeDV : null;
    if (Number.isFinite(rangePercent)) {
      return rangePercent;
    }

    const todayPercent =
      typeof node.todayDV === "number" ? node.todayDV : null;
    if (Number.isFinite(todayPercent)) {
      return todayPercent;
    }

    return null;
  }

  /**
   * Vitamins / Minerals:
   *  - Average % of targets reached in this category.
   *  - Number of "low" and "high/caution" nutrients.
   */
  buildMicronutrientOverview(categoryId, label, nutrientOptions) {
    const ids = (nutrientOptions || [])
      .map((option) => option && option.id)
      .filter(Boolean);

    let totalPercent = 0;
    let percentCount = 0;
    let lowCount = 0;
    let highOrCautionCount = 0;

    ids.forEach((nutrientId) => {
      const node = this.nutritionData[nutrientId];
      if (!node || typeof node !== "object") {
        return;
      }

      const percentOfTarget = this.getPercentOfTargetForNode(node);
      if (percentOfTarget != null && Number.isFinite(percentOfTarget)) {
        totalPercent += percentOfTarget;
        percentCount += 1;
      }

      const status = this.statuses[nutrientId];
      if (status === "low") {
        lowCount += 1;
      } else if (status === "high" || status === "caution") {
        highOrCautionCount += 1;
      }
    });

    if (percentCount === 0 && lowCount === 0 && highOrCautionCount === 0) {
      return {
        id: categoryId,
        label,
        meta: "No logged data in range yet",
      };
    }

    const averagePercent =
      percentCount > 0 ? totalPercent / percentCount : null;

    const clampedAverage =
      averagePercent != null && Number.isFinite(averagePercent)
        ? Math.max(0, Math.min(160, averagePercent))
        : null;

    const roundedAverage =
      clampedAverage != null && Number.isFinite(clampedAverage)
        ? Math.round(clampedAverage)
        : null;

    const coverageLabel =
      roundedAverage != null
        ? `Avg ${roundedAverage}% of targets`
        : "Coverage based on partial data";

    const lowLabel =
      lowCount > 0 ? `${lowCount} low` : "no low nutrients";

    const highLabel =
      highOrCautionCount > 0
        ? `, ${highOrCautionCount} high/caution`
        : "";

    return {
      id: categoryId,
      label,
      meta: `${coverageLabel} • ${lowLabel}${highLabel}`,
    };
  }

  /**
   * Fats:
   *  - Shows total fat as % of energy with the personalised goal range.
   *  - Adds a short qualitative tag: below / within / above typical range.
   */
  buildFatOverview() {
    const node = this.nutritionData.totalFat;
    const status = this.statuses.totalFat;

    if (!node || typeof node !== "object") {
      return {
        id: "fats",
        label: "Fats",
        meta: "No fat data in range yet",
      };
    }

    const rawEnergyPercent =
      typeof node.ePct === "number" ? node.ePct : null;
    const energyPercent =
      rawEnergyPercent != null && Number.isFinite(rawEnergyPercent)
        ? Math.round(rawEnergyPercent)
        : null;

    const minimum =
      typeof node.targetMin === "number" && Number.isFinite(node.targetMin)
        ? node.targetMin
        : null;
    const maximum =
      typeof node.targetMax === "number" && Number.isFinite(node.targetMax)
        ? node.targetMax
        : null;

    let targetLabel = null;
    if (minimum != null && maximum != null) {
      targetLabel = `${minimum}–${maximum}% E`;
    } else if (minimum != null) {
      targetLabel = `≥ ${minimum}% E`;
    } else if (maximum != null) {
      targetLabel = `≤ ${maximum}% E`;
    }

    if (energyPercent == null) {
      const genericMeta =
        targetLabel != null
          ? `Target: ${targetLabel}`
          : "Coverage based on partial fat data";

      return {
        id: "fats",
        label: "Fats",
        meta: genericMeta,
      };
    }

    // Prefer the model status if it already flags this fat pattern.
    let qualitativeSuffix = "";
    if (status === "low") {
      qualitativeSuffix = " • below typical range";
    } else if (status === "high" || status === "caution") {
      qualitativeSuffix = " • above typical range";
    } else if (minimum != null || maximum != null) {
      if (minimum != null && energyPercent < minimum) {
        qualitativeSuffix = " • below typical range";
      } else if (maximum != null && energyPercent > maximum) {
        qualitativeSuffix = " • above typical range";
      } else {
        qualitativeSuffix = " • within typical range";
      }
    }

    const rangeDisplay =
      targetLabel != null ? ` (goal ${targetLabel})` : "";

    const meta = `${energyPercent}% ${rangeDisplay}${qualitativeSuffix}`;

    return {
      id: "fats",
      label: "Fats",
      meta,
    };
  }
}

export function getCategoryOverview(dataInput) {
  const data = dataInput || initialData;
  const statuses = getStatuses(data);

  const builder = new CategoryOverviewBuilder({
    nutritionData: data,
    statuses,
  });

  const vitaminsMeta = builder.buildMicronutrientOverview(
    "vitamins",
    "Vitamins",
    vitaminOptions,
  );

  const mineralsMeta = builder.buildMicronutrientOverview(
    "minerals",
    "Minerals",
    mineralOptions,
  );

  const fatsMeta = builder.buildFatOverview();

  // Keep using the existing fitness/body overview helper so you do not
  // lose any special handling you already have there.
  const [fitnessMeta, bodyMeta] = getFitnessBodyOverviewMetas(data);

  const overviewItems = [];

  if (vitaminsMeta) overviewItems.push(vitaminsMeta);
  if (mineralsMeta) overviewItems.push(mineralsMeta);
  if (fatsMeta) overviewItems.push(fatsMeta);
  if (fitnessMeta) overviewItems.push(fitnessMeta);
  if (bodyMeta) overviewItems.push(bodyMeta);

  return overviewItems;
}

export function getGroups(_dataInput) {
  return [
    getVitaminsGroup(),
    getMineralsGroup(),
    getFatsGroup(),
    ...getFitnessBodyGroups(),
  ];
}

export function getInsights(_dataInput) {
  return [];
}

export function getQuickAddDefaults(dataInput) {
  const data = dataInput || initialData;
  const focusId = getFocusOrder(data)[0];
  const vitaminDefaults = getVitaminQuickAddDefaults(focusId, data);

  return {
    targetId: focusId,
    amount: vitaminDefaults?.amount ?? 1,
  };
}

export function getQuickAddBehaviorFor(id, dataInput) {
  const vitaminBehavior = getVitaminQuickAddBehaviorFor(id, dataInput);
  if (vitaminBehavior) return vitaminBehavior;

  return {
    mode: "dvNudge",
    deltaPct: 10,
    unit: null,
    unitLabel: null,
  };
}

export function buildTileSpec({ id, label, data, statuses }) {
  const node = data?.[id];
  if (!node) return null;

  const fitnessBodyTile = buildFitnessBodySpecialTile(id, node, statuses, label);
  if (fitnessBodyTile) return fitnessBodyTile;

  const mineralSpecialTile = buildMineralSpecialTile(id, node, statuses, label);
  if (mineralSpecialTile) return mineralSpecialTile;

  const vitaminSpecialTile = buildVitaminSpecialTile(id, node, statuses, label);
  if (vitaminSpecialTile) return vitaminSpecialTile;

  const fatSpecialTile = buildFatSpecialTile(id, node, statuses, label);
  if (fatSpecialTile) return fatSpecialTile;

  // Fats (E%) – make them behave like vitamins/minerals in the grid but let
  // the shell decide meta controls based on `details` presence.
  const isFat = FAT_IDS.has(id);
  if (isFat && node.ePct != null) {
    const minimum = node.targetMin;
    const maximum = node.targetMax;

    const targetLabel =
      minimum != null && maximum != null
        ? `${minimum}–${maximum}% E`
        : minimum != null
          ? `≥ ${minimum}% E`
          : maximum != null
            ? `≤ ${maximum}% E`
            : null;

    const percentOfDailyValue = Number.isFinite(node.todayDV) ? node.todayDV : 0;
    const stroke = ringStrokeByStatus(statuses[id]);
    const helperText = targetLabel ? `Target: ${targetLabel}` : null;

    return {
      id,
      label,
      status: statuses[id],
      hero: {
        type: "ring",
        pct: percentOfDailyValue,
        mainText: `${node.ePct}% E`,
        subText: targetLabel || "",
        stroke,
        ariaLabel: `${label} ${percentOfDailyValue}%`,
      },
      // For fats we always let the meta button be the medical clipboard by
      // leaving `details` null and providing an `open` action.
      helper: helperText,
      details: null,
      actions: {
        open: { id, label },
        log:
          getFatLogAction(id) ?? {
            mode: "dvNudge",
            nutrientId: id,
            deltaPct: 10,
          },
      },
    };
  }

  // Default DV-based tiles (vitamins, minerals, etc.).
  const { mainText, subText } = formatAmountForDisplay(node);
  const percentOfDailyValue = Number.isFinite(node.todayDV) ? node.todayDV : 0;
  const stroke = ringStrokeByStatus(statuses[id]);

  const detailsBits = [];

  const targetDetail = getNutrientTargetDetail(node);
  if (targetDetail) {
    detailsBits.push(targetDetail);
  }

  const upperDetail = getNutrientUpperLevelDetail(node);
  if (upperDetail) {
    detailsBits.push(upperDetail);
  }

  const detailsText = detailsBits.join(" • ");

  const fatLogAction = getFatLogAction(id);
  const vitaminLogAction = getVitaminLogAction(id);

  const logAction =
    fatLogAction ??
    vitaminLogAction ?? {
      mode: "dvNudge",
      nutrientId: id,
      deltaPct: 10,
    };

  const status = statuses[id];

  // We only use inline "details" (chevron + expandable block) when the
  // nutrient is on-track or data-needed. For low/high/caution we want the
  // medical clipboard meta button, so we leave `details` null and rely on
  // the `open` action just like in your Vitamin C example.
  const shouldShowInlineDetails =
    status === "on-track" || status === "data-needed";

  return {
    id,
    label,
    status,
    hero: {
      type: "ring",
      pct: percentOfDailyValue,
      mainText,
      subText,
      stroke,
      ariaLabel: `${label} ${percentOfDailyValue}%`,
    },
    helper: null,
    details:
      shouldShowInlineDetails && detailsText
        ? {
          type: "mutedText",
          text: detailsText,
        }
        : null,
    actions: {
      open: { id, label },
      log: logAction,
    },
  };
}
